import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  ApiError,
  getDashboard,
  getTargets,
  updateTargets,
  type Dashboard,
  type DashboardDimension,
  type EnrichedHolding,
  type Property,
  type Targets,
  type Vehicle,
} from '../api';
import { Card } from '../components/Card';
import { DimensionChart } from '../components/DimensionChart';
import { InvestmentsSection } from '../components/InvestmentsSection';
import { ChatLauncher } from '../components/ChatLauncher';
import { PropertiesSection } from '../components/PropertiesSection';
import { TargetsEditor } from '../components/TargetsEditor';
import { ContributionsSection } from '../components/ContributionsSection';
import { ProjectionSection } from '../components/ProjectionSection';
import { listVehicles } from '../api';

const REBALANCE_THRESHOLD_PP = 5;

type DashboardTab = 'investments' | 'property' | 'projection' | 'charts' | 'contributions';
const TAB_DEFS: { id: DashboardTab; label: string }[] = [
  { id: 'investments', label: 'Investments' },
  { id: 'property', label: 'Property' },
  { id: 'projection', label: 'Retirement projection' },
  { id: 'charts', label: 'Charts' },
  { id: 'contributions', label: 'Contributions' },
];
const TAB_IDS: DashboardTab[] = TAB_DEFS.map((t) => t.id);

function parseTab(v: string | null): DashboardTab {
  if (!v) return 'investments';
  return (TAB_IDS as readonly string[]).includes(v) ? (v as DashboardTab) : 'investments';
}

// Map alert scrollTo ids to the tab they live in, so clicking a pill in
// Needs attention switches to the right tab before scrolling.
function tabForScrollTarget(id: string): DashboardTab {
  if (id === 'investments') return 'investments';
  if (id === 'property') return 'property';
  if (id === 'contributions') return 'contributions';
  if (id === 'projection') return 'projection';
  if (id.startsWith('chart-')) return 'charts';
  return 'investments';
}

type PerformanceFilter = 'well' | 'average' | 'poor' | 'disastrous' | null;
const PERF_KEYS: ReadonlyArray<Exclude<PerformanceFilter, null>> = [
  'well',
  'average',
  'poor',
  'disastrous',
];

function parsePerf(v: string | null): PerformanceFilter {
  if (!v) return null;
  return (PERF_KEYS as readonly string[]).includes(v) ? (v as PerformanceFilter) : null;
}

export default function DashboardPage() {
  const { accountId = '' } = useParams<{ accountId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const [dashboard, setDashboard] = useState<Dashboard | null>(null);
  const [targets, setTargets] = useState<Targets | null>(null);
  const [holdings, setHoldings] = useState<EnrichedHolding[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [properties, setProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const perfFilter = parsePerf(searchParams.get('perf'));
  const setPerfFilter = useCallback(
    (next: PerformanceFilter) => {
      setSearchParams(
        (prev) => {
          const out = new URLSearchParams(prev);
          if (next) out.set('perf', next);
          else out.delete('perf');
          return out;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const activeTab = parseTab(searchParams.get('tab'));
  const setActiveTab = useCallback(
    (next: DashboardTab) => {
      setSearchParams(
        (prev) => {
          const out = new URLSearchParams(prev);
          if (next === 'investments') out.delete('tab');
          else out.set('tab', next);
          return out;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Holdings + vehicles come from InvestmentsSection's own fetch via
      // onPortfolioLoad — we only fetch the dashboard rollup + targets here.
      const [d, t] = await Promise.all([getDashboard(accountId), getTargets(accountId)]);
      setDashboard(d);
      setTargets(t);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setError('You do not have access to this household.');
      } else {
        setError(err instanceof Error ? err.message : 'Failed to load');
      }
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Honour #investments / #chart-* anchors from alert deep-links: scroll
  // once after first render so anchored navigation lands the user where
  // the alert pointed.
  useEffect(() => {
    if (loading) return;
    const hash = window.location.hash.replace('#', '');
    if (!hash) return;
    const el = document.getElementById(hash);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [loading]);

  async function handleSave(next: Targets) {
    setSaving(true);
    try {
      const saved = await updateTargets(accountId, next);
      setTargets(saved);
      setEditing(false);
      const d = await getDashboard(accountId);
      setDashboard(d);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  const portfolio = useMemo(() => computePortfolio(holdings, vehicles), [holdings, vehicles]);
  const alerts = useMemo(
    () => (dashboard ? buildAlerts(dashboard, holdings) : []),
    [dashboard, holdings]
  );
  const annualCostPence = useMemo(
    () => computeAnnualCostPence(vehicles, holdings),
    [vehicles, holdings]
  );

  const refreshVehicles = useCallback(async () => {
    try {
      const { vehicles: vs } = await listVehicles(accountId);
      setVehicles(vs);
    } catch (err) {
      // Non-fatal: the user just made an edit; surface a soft error.
      console.warn('refresh vehicles failed', err);
    }
  }, [accountId]);

  if (loading && !dashboard) return <p>Loading…</p>;
  if (error)
    return (
      <Card>
        <p style={{ color: '#b91c1c', margin: 0 }}>{error}</p>
        <p style={{ marginBottom: 0 }}>
          <Link to="/">← back home</Link>
        </p>
      </Card>
    );
  if (!dashboard || !targets) return null;

  return (
    <div>
      <Card
        title={<span style={{ fontSize: '1.4rem', fontWeight: 700 }}>Dashboard</span>}
        subtitle={portfolio.lastUpdated ? `As of ${portfolio.lastUpdated}` : undefined}
      >
        <KpiGrid
          stats={[
            { label: 'Total value', value: formatGBP(dashboard.total_value_pence) },
            {
              label: 'Total gain',
              value:
                portfolio.totalGainPence != null ? formatSignedGBP(portfolio.totalGainPence) : '—',
              sub:
                portfolio.totalGainPct != null
                  ? `${portfolio.totalGainPct >= 0 ? '+' : ''}${portfolio.totalGainPct.toFixed(2)}%`
                  : undefined,
              tone:
                portfolio.totalGainPence == null
                  ? 'neutral'
                  : portfolio.totalGainPence >= 0
                    ? 'positive'
                    : 'negative',
            },
            {
              label: 'Investments',
              value: String(holdings.length),
              sub: `across ${vehicles.length} ${vehicles.length === 1 ? 'vehicle' : 'vehicles'}`,
            },
            {
              label: 'Avg annualised',
              value: portfolio.avgGrowth != null ? `${portfolio.avgGrowth.toFixed(2)}%` : '—',
              sub:
                portfolio.avgGrowth == null
                  ? 'needs ≥180 days of history'
                  : 'value-weighted across qualifying holdings',
              tone:
                portfolio.avgGrowth == null
                  ? 'neutral'
                  : portfolio.avgGrowth >= 6
                    ? 'positive'
                    : portfolio.avgGrowth >= 1
                      ? 'warning'
                      : 'negative',
            },
            {
              label: 'Annual cost',
              value: annualCostPence > 0 ? formatGBP(annualCostPence) : '—',
              sub:
                dashboard.total_value_pence > 0 && annualCostPence > 0
                  ? `${((annualCostPence / dashboard.total_value_pence) * 100).toFixed(2)}% all-in`
                  : 'set platform fees in Costs',
              tone:
                annualCostPence === 0
                  ? 'neutral'
                  : dashboard.total_value_pence > 0 &&
                      annualCostPence / dashboard.total_value_pence >= 0.01
                    ? 'negative'
                    : dashboard.total_value_pence > 0 &&
                        annualCostPence / dashboard.total_value_pence >= 0.005
                      ? 'warning'
                      : 'positive',
            },
          ]}
        />
      </Card>

      {editing && (
        <Card title="Edit targets">
          <TargetsEditor
            initial={targets}
            dashboard={dashboard}
            onSave={handleSave}
            onCancel={() => setEditing(false)}
            saving={saving}
          />
        </Card>
      )}

      {alerts.length > 0 && <AlertsTiles alerts={alerts} onAlertClick={handleAlertClick} />}

      <TabBar
        active={activeTab}
        onChange={setActiveTab}
        tabs={TAB_DEFS}
        rightActions={
          <>
            <LinkButton to={`/accounts/${encodeURIComponent(accountId)}/funds`}>Funds →</LinkButton>
            {!editing && <button onClick={() => setEditing(true)}>Edit targets</button>}
          </>
        }
      />

      <div hidden={activeTab !== 'investments'}>
        <div id="investments">
          <Card title="Investments">
            <InvestmentsSection
              accountId={accountId}
              perfFilter={perfFilter}
              onPerfFilterChange={setPerfFilter}
              onPortfolioLoad={({ holdings: h, vehicles: v }) => {
                setHoldings(h);
                setVehicles(v);
              }}
            />
          </Card>
        </div>
      </div>

      <div hidden={activeTab !== 'property'}>
        <div id="property">
          <Card
            title="Property"
            subtitle="Real-estate assets revalued manually. Folds into the Asset Allocation chart's Property bucket."
          >
            <PropertiesSection
              accountId={accountId}
              onPropertiesLoad={({ properties: ps }) => {
                setProperties(ps);
                refresh();
              }}
            />
          </Card>
        </div>
      </div>

      <div hidden={activeTab !== 'projection'}>
        <div id="projection">
          <Card
            title="Retirement projection"
            subtitle="Compounds the current portfolio + your planned annual contributions. Adjust inputs — chart updates live."
          >
            <ProjectionSection
              accountId={accountId}
              vehicles={vehicles}
              holdings={holdings}
              properties={properties}
              onVehicleSaved={refreshVehicles}
            />
          </Card>
        </div>
      </div>

      <div hidden={activeTab !== 'charts'}>
        <div id="chart-wealth">
          <Card>
            <DimensionChart
              title="Wealth Allocation"
              subtitle="Split across household members."
              dimension={dashboard.wealth}
            />
          </Card>
        </div>
        <div id="chart-asset">
          <Card>
            <DimensionChart
              title="Asset Allocation"
              subtitle="Equity / Bonds / Cash / etc — derived from each fund's category."
              dimension={dashboard.asset}
            />
          </Card>
        </div>
        <div id="chart-geo">
          <Card>
            <DimensionChart
              title="Geographic Allocation"
              subtitle="Region the underlying companies sit in."
              dimension={dashboard.geo}
            />
          </Card>
        </div>
        <div id="chart-sector">
          <Card>
            <DimensionChart
              title="Sector Diversification"
              subtitle="GICS sector mix from each fund's allocation breakdown."
              dimension={dashboard.sector}
            />
          </Card>
        </div>
        <div id="chart-risk">
          <Card>
            <DimensionChart
              title="Risk Allocation"
              subtitle="1 = cash-like, 10 = single-country / crypto. Derived from category."
              dimension={dashboard.risk}
              bucketLabel="Risk"
              sortMode="numericAsc"
            />
          </Card>
        </div>
      </div>

      <div hidden={activeTab !== 'contributions'}>
        <div id="contributions">
          <Card
            title="Contributions"
            subtitle="Track AA / ISA usage by tax year. Source = employer (company contribution) or personal."
          >
            <ContributionsSection
              accountId={accountId}
              vehicles={vehicles}
              onChange={refreshVehicles}
            />
          </Card>
        </div>
      </div>

      <ChatLauncher accountId={accountId} />
    </div>
  );

  function handleAlertClick(a: Alert) {
    if (a.perfFilter !== undefined) {
      setPerfFilter(a.perfFilter);
    }
    if (a.scrollTo) {
      setActiveTab(tabForScrollTarget(a.scrollTo));
      // Defer scroll so the just-revealed tab content has a chance to lay
      // out — getBoundingClientRect is wrong on `hidden` elements until
      // the next paint.
      const target = a.scrollTo;
      requestAnimationFrame(() => {
        const el = document.getElementById(target);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }
}

// ---------------------------------------------------------------------------
// TabBar — sits between Needs attention and the active section
// ---------------------------------------------------------------------------

function TabBar({
  tabs,
  active,
  onChange,
  rightActions,
}: {
  tabs: { id: DashboardTab; label: string }[];
  active: DashboardTab;
  onChange: (id: DashboardTab) => void;
  rightActions?: React.ReactNode;
}) {
  return (
    <div
      role="tablist"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.25rem',
        borderBottom: '1px solid #e2e8f0',
        margin: '1rem 0',
        flexWrap: 'wrap',
      }}
    >
      {tabs.map((t) => {
        const selected = t.id === active;
        return (
          <button
            key={t.id}
            role="tab"
            aria-selected={selected}
            onClick={() => onChange(t.id)}
            style={{
              background: 'transparent',
              border: 'none',
              padding: '0.6rem 1rem',
              cursor: 'pointer',
              fontWeight: selected ? 600 : 500,
              color: selected ? '#0f172a' : '#64748b',
              borderBottom: selected ? '2px solid #0f172a' : '2px solid transparent',
              marginBottom: '-1px',
              fontSize: '0.95rem',
              fontFamily: 'inherit',
            }}
          >
            {t.label}
          </button>
        );
      })}
      {rightActions && (
        <div
          style={{
            marginLeft: 'auto',
            display: 'flex',
            gap: '0.5rem',
            alignItems: 'center',
            paddingBottom: '0.4rem',
          }}
        >
          {rightActions}
        </div>
      )}
    </div>
  );
}

// computeAnnualCostPence sums fund-OCF cost plus per-vehicle platform
// fees. Lives here so the headline KPI doesn't depend on the (now
// removed) standalone Costs section.
function computeAnnualCostPence(vehicles: Vehicle[], holdings: EnrichedHolding[]): number {
  const byVehicle = new Map<string, EnrichedHolding[]>();
  for (const h of holdings) {
    if (!h.vehicle_id) continue;
    const arr = byVehicle.get(h.vehicle_id) ?? [];
    arr.push(h);
    byVehicle.set(h.vehicle_id, arr);
  }
  let total = 0;
  for (const v of vehicles) {
    const hs = byVehicle.get(v.id) ?? [];
    let market = v.cash_pence ?? 0;
    for (const h of hs) {
      const mv = h.market_value_pence ?? 0;
      market += mv;
      total += (mv * (h.fund_ocf_pct ?? 0)) / 100;
    }
    total += (market * (v.platform_fee_pct_per_year ?? 0)) / 100;
    total += v.platform_fee_flat_pence ?? 0;
    total += (market * (v.advisor_fee_pct_per_year ?? 0)) / 100;
  }
  return Math.round(total);
}

// ---------------------------------------------------------------------------
// LinkButton — Link styled as a button (avoids nesting <button> in <a>)
// ---------------------------------------------------------------------------

function LinkButton({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <Link
      to={to}
      style={{
        display: 'inline-block',
        padding: '0.4rem 0.8rem',
        border: '1px solid #cbd5e1',
        background: '#fff',
        borderRadius: 6,
        color: '#334155',
        textDecoration: 'none',
        fontSize: '0.875rem',
        lineHeight: 1.2,
      }}
    >
      {children}
    </Link>
  );
}

// ---------------------------------------------------------------------------
// KPI grid
// ---------------------------------------------------------------------------

type KpiTone = 'neutral' | 'positive' | 'negative' | 'warning';

interface Kpi {
  label: string;
  value: string;
  sub?: string;
  tone?: KpiTone;
}

function KpiGrid({ stats }: { stats: Kpi[] }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: '0.75rem',
      }}
    >
      {stats.map((s, i) => (
        <div
          key={i}
          style={{
            background: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderRadius: 10,
            padding: '0.85rem 1rem',
          }}
        >
          <div
            style={{
              fontSize: '0.72rem',
              fontWeight: 600,
              color: '#64748b',
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
            }}
          >
            {s.label}
          </div>
          <div
            style={{
              fontSize: '1.5rem',
              fontWeight: 700,
              marginTop: '0.25rem',
              color: toneColour(s.tone),
            }}
          >
            {s.value}
          </div>
          {s.sub && (
            <div
              style={{
                fontSize: '0.78rem',
                color: s.tone && s.tone !== 'neutral' ? toneColour(s.tone) : '#64748b',
                marginTop: '0.15rem',
              }}
            >
              {s.sub}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function toneColour(tone?: KpiTone): string {
  switch (tone) {
    case 'positive':
      return '#059669';
    case 'negative':
      return '#dc2626';
    case 'warning':
      return '#b45309';
    default:
      return '#0f172a';
  }
}

// ---------------------------------------------------------------------------
// Alerts
// ---------------------------------------------------------------------------

type AlertSeverity = 'info' | 'warn' | 'danger';
type AlertGroup = 'Performance' | 'Wealth' | 'Asset' | 'Geo' | 'Sector' | 'Risk' | 'Data';

const ALERT_GROUP_ORDER: AlertGroup[] = [
  'Performance',
  'Wealth',
  'Asset',
  'Geo',
  'Sector',
  'Risk',
  'Data',
];

interface Alert {
  severity: AlertSeverity;
  group: AlertGroup;
  title: string;
  detail?: string;
  // perfFilter, when set, drives the embedded InvestmentsSection's filter
  // when the user clicks the pill. undefined = leave filter alone.
  perfFilter?: PerformanceFilter;
  // scrollTo, when set, is the DOM id to scroll into view on click.
  scrollTo?: string;
}

function buildAlerts(d: Dashboard, holdings: EnrichedHolding[]): Alert[] {
  const out: Alert[] = [];

  // Performance — only counts holdings with a computed annualised return
  // (so sub-180-day rows don't get smeared into "disastrous").
  const rated = holdings.filter((h) => h.growth_pct_per_year != null);
  const disastrous = rated.filter((h) => (h.growth_pct_per_year ?? 0) < 1).length;
  const poor = rated.filter(
    (h) => (h.growth_pct_per_year ?? 0) >= 1 && (h.growth_pct_per_year ?? 0) < 6
  ).length;
  if (disastrous > 0) {
    out.push({
      severity: 'danger',
      group: 'Performance',
      title: `${disastrous} ${disastrous === 1 ? 'investment' : 'investments'} performing disastrously`,
      detail: 'Annualised return below 1%. Filter the table to see them.',
      perfFilter: 'disastrous',
      scrollTo: 'investments',
    });
  }
  if (poor > 0) {
    out.push({
      severity: 'warn',
      group: 'Performance',
      title: `${poor} ${poor === 1 ? 'investment' : 'investments'} performing poorly`,
      detail: 'Annualised return between 1% and 6%.',
      perfFilter: 'poor',
      scrollTo: 'investments',
    });
  }

  // Rebalancing — surface every bucket above the threshold, sorted by
  // severity within each dimension. One pill per breach so nothing hides
  // behind a worse offender in the same bucket group.
  const dimensions: { group: AlertGroup; key: string; dim: DashboardDimension }[] = [
    { group: 'Wealth', key: 'wealth', dim: d.wealth },
    { group: 'Asset', key: 'asset', dim: d.asset },
    { group: 'Geo', key: 'geo', dim: d.geo },
    { group: 'Sector', key: 'sector', dim: d.sector },
    { group: 'Risk', key: 'risk', dim: d.risk },
  ];
  for (const { group, key, dim } of dimensions) {
    const breaches = dim.buckets
      .map((b) => ({
        label: b.label,
        current: b.current_pct,
        desired: b.desired_pct,
        delta: Math.abs(b.current_pct - b.desired_pct),
      }))
      .filter((b) => b.delta >= REBALANCE_THRESHOLD_PP)
      .sort((a, b) => b.delta - a.delta);
    for (const b of breaches) {
      const drift = b.current > b.desired ? 'over' : 'under';
      out.push({
        severity: b.delta >= 15 ? 'danger' : 'warn',
        group,
        title: `${b.label} ${drift} target`,
        detail: `${b.current.toFixed(1)}% vs ${b.desired.toFixed(1)}% target (${b.delta.toFixed(1)}pp off).`,
        scrollTo: `chart-${key}`,
      });
    }
  }

  // Unknowns — sum across dimensions, dedupe by fund_id so a fund missing
  // both asset class and sector doesn't get counted twice in the £ total.
  const unknownByFund = new Map<string, { name: string; value: number }>();
  let unknownsLandedIn: string | null = null;
  for (const [key, dim] of [
    ['asset', d.asset] as const,
    ['geo', d.geo] as const,
    ['sector', d.sector] as const,
    ['risk', d.risk] as const,
  ]) {
    for (const f of dim.unknown_funds ?? []) {
      const prior = unknownByFund.get(f.fund_id);
      if (!prior || f.value_pence > prior.value) {
        unknownByFund.set(f.fund_id, { name: f.fund_name, value: f.value_pence });
      }
      if (unknownsLandedIn == null) unknownsLandedIn = `chart-${key}`;
    }
  }
  if (unknownByFund.size > 0) {
    const total = Array.from(unknownByFund.values()).reduce((s, x) => s + x.value, 0);
    out.push({
      severity: 'info',
      group: 'Data',
      title: `${unknownByFund.size} ${unknownByFund.size === 1 ? 'fund' : 'funds'} missing allocation data`,
      detail: `${formatGBP(total)} sits in "Unknown" buckets — add allocation data to fix.`,
      scrollTo: unknownsLandedIn ?? 'chart-asset',
    });
  }

  return out;
}

function AlertsTiles({
  alerts,
  onAlertClick,
}: {
  alerts: Alert[];
  onAlertClick: (a: Alert) => void;
}) {
  const grouped = useMemo(() => {
    const map = new Map<AlertGroup, Alert[]>();
    for (const a of alerts) {
      const arr = map.get(a.group) ?? [];
      arr.push(a);
      map.set(a.group, arr);
    }
    return ALERT_GROUP_ORDER.filter((g) => map.has(g)).map((g) => ({
      group: g,
      items: map.get(g)!,
    }));
  }, [alerts]);

  const [openGroup, setOpenGroup] = useState<AlertGroup | null>(null);

  // Close on Escape — modal-y behaviour.
  useEffect(() => {
    if (!openGroup) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpenGroup(null);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [openGroup]);

  const openItems = openGroup ? (grouped.find((g) => g.group === openGroup)?.items ?? []) : [];

  return (
    <>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${grouped.length}, minmax(0, 1fr))`,
          gap: '0.5rem',
          margin: '0.5rem 0',
        }}
      >
        {grouped.map(({ group, items }) => {
          const worst = items.reduce<AlertSeverity>(
            (acc, a) => (severityRank(a.severity) > severityRank(acc) ? a.severity : acc),
            'info'
          );
          const tone = alertTone(worst);
          return (
            <button
              key={group}
              onClick={() => setOpenGroup(group)}
              style={{
                background: tone.bg,
                border: `1px solid ${tone.border}`,
                borderLeft: `4px solid ${tone.accent}`,
                borderRadius: 10,
                padding: '0.6rem 0.85rem',
                cursor: 'pointer',
                fontFamily: 'inherit',
                textAlign: 'left',
              }}
              title={summariseGroup(items)}
            >
              <div
                style={{
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  color: tone.title,
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                }}
              >
                {group}
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: '0.4rem',
                  marginTop: '0.2rem',
                }}
              >
                <span
                  style={{
                    fontSize: '1.3rem',
                    fontWeight: 700,
                    color: tone.title,
                  }}
                >
                  {items.length}
                </span>
                <span style={{ fontSize: '0.78rem', color: '#475569' }}>
                  {summariseGroup(items)}
                </span>
              </div>
            </button>
          );
        })}
      </div>

      {openGroup && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={`${openGroup} alerts`}
          onClick={() => setOpenGroup(null)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1rem',
            zIndex: 100,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white',
              borderRadius: '0.75rem',
              boxShadow: '0 20px 60px rgba(15, 23, 42, 0.35)',
              width: '100%',
              maxWidth: '640px',
              maxHeight: '85vh',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                padding: '0.85rem 1.1rem',
                borderBottom: '1px solid #e2e8f0',
              }}
            >
              <div style={{ fontWeight: 700, fontSize: '1.05rem' }}>{openGroup}</div>
              <div style={{ flex: 1, fontSize: '0.85rem', color: '#64748b' }}>
                {openItems.length} item{openItems.length === 1 ? '' : 's'}
                {' · '}
                {summariseGroup(openItems)}
              </div>
              <button
                onClick={() => setOpenGroup(null)}
                aria-label="Close"
                style={{
                  background: 'transparent',
                  border: 'none',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  color: '#64748b',
                  lineHeight: 1,
                }}
              >
                ×
              </button>
            </div>
            <div
              style={{
                flex: 1,
                minHeight: 0,
                overflow: 'auto',
                padding: '1rem',
                display: 'flex',
                flexDirection: 'column',
                gap: '0.5rem',
              }}
            >
              {openItems.map((a, i) => (
                <AlertPill
                  key={i}
                  alert={a}
                  onClick={() => {
                    setOpenGroup(null);
                    onAlertClick(a);
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function severityRank(s: AlertSeverity): number {
  return s === 'danger' ? 2 : s === 'warn' ? 1 : 0;
}

function summariseGroup(items: Alert[]): string {
  const dangers = items.filter((a) => a.severity === 'danger').length;
  const warns = items.filter((a) => a.severity === 'warn').length;
  const infos = items.filter((a) => a.severity === 'info').length;
  const parts: string[] = [];
  if (dangers > 0) parts.push(`${dangers} critical`);
  if (warns > 0) parts.push(`${warns} warning${warns === 1 ? '' : 's'}`);
  if (infos > 0) parts.push(`${infos} info`);
  return parts.join(' · ');
}

function AlertPill({ alert, onClick }: { alert: Alert; onClick: () => void }) {
  const tone = alertTone(alert.severity);
  return (
    <button
      onClick={onClick}
      style={{
        display: 'block',
        textAlign: 'left',
        width: '100%',
        background: tone.bg,
        border: `1px solid ${tone.border}`,
        borderLeft: `4px solid ${tone.accent}`,
        borderRadius: 10,
        padding: '0.7rem 0.9rem',
        cursor: 'pointer',
        fontFamily: 'inherit',
      }}
    >
      <div style={{ fontWeight: 600, color: tone.title, fontSize: '0.9rem' }}>{alert.title}</div>
      {alert.detail && (
        <div style={{ fontSize: '0.8rem', color: '#475569', marginTop: '0.2rem' }}>
          {alert.detail}
        </div>
      )}
    </button>
  );
}

function alertTone(s: AlertSeverity) {
  switch (s) {
    case 'danger':
      return { bg: '#fef2f2', border: '#fecaca', accent: '#dc2626', title: '#991b1b' };
    case 'warn':
      return { bg: '#fffbeb', border: '#fde68a', accent: '#d97706', title: '#92400e' };
    default:
      return { bg: '#f1f5f9', border: '#e2e8f0', accent: '#64748b', title: '#334155' };
  }
}

// ---------------------------------------------------------------------------
// Portfolio rollup (uses holdings/vehicles populated by InvestmentsSection)
// ---------------------------------------------------------------------------

interface PortfolioSummary {
  totalGainPence: number | null;
  totalGainPct: number | null;
  avgGrowth: number | null;
  lastUpdated: string | null;
}

function computePortfolio(holdings: EnrichedHolding[], vehicles: Vehicle[]): PortfolioSummary {
  if (holdings.length === 0 && vehicles.length === 0) {
    return { totalGainPence: null, totalGainPct: null, avgGrowth: null, lastUpdated: null };
  }
  let book = 0;
  let market = 0;
  let allValued = true;
  for (const v of vehicles) {
    book += v.cash_pence;
    market += v.cash_pence;
  }
  for (const h of holdings) {
    book += h.book_cost_pence;
    if (h.market_value_pence != null) market += h.market_value_pence;
    else allValued = false;
  }
  const totalGainPence = allValued ? market - book : null;
  const totalGainPct = allValued && book > 0 ? ((market - book) / book) * 100 : null;
  const avgGrowth = weightedAvgGrowth(holdings);

  let latest: string | null = null;
  for (const h of holdings) {
    if (h.price_as_of && (latest == null || h.price_as_of > latest)) latest = h.price_as_of;
  }
  return { totalGainPence, totalGainPct, avgGrowth, lastUpdated: latest };
}

function weightedAvgGrowth(rows: EnrichedHolding[]): number | null {
  let totalMv = 0;
  let weighted = 0;
  for (const h of rows) {
    if (h.growth_pct_per_year == null || h.market_value_pence == null) continue;
    totalMv += h.market_value_pence;
    weighted += h.market_value_pence * h.growth_pct_per_year;
  }
  if (totalMv === 0) return null;
  return weighted / totalMv;
}

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

const gbp = new Intl.NumberFormat('en-GB', {
  style: 'currency',
  currency: 'GBP',
  maximumFractionDigits: 2,
});

function formatGBP(pence: number): string {
  return gbp.format(pence / 100);
}

function formatSignedGBP(pence: number): string {
  return `${pence >= 0 ? '+' : ''}${gbp.format(pence / 100)}`;
}
